"use client"

import {
    ArrowDown01Icon,
    ArrowUp01Icon,
    ArrowUpRightIcon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { motion, useMotionValue, useSpring } from "framer-motion"
import Image from "next/image"
import { useEffect, useState } from "react"
import { ProjectDialog } from "@/components/sections/project-dialog"
import { trackProjectClick } from "@/lib/analytics"
import { PROJECTS_DATA } from "@/lib/projects-data"

export function ProjectGalleryMinimal({
    showImageOnHover = true,
}: {
    showImageOnHover?: boolean
}) {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [showAllProjects, setShowAllProjects] = useState(false)

    const INITIAL_VISIBLE_COUNT = 6

    const displayedProjects = showAllProjects
        ? PROJECTS_DATA
        : PROJECTS_DATA.slice(0, INITIAL_VISIBLE_COUNT)
    const hasMoreProjects = PROJECTS_DATA.length > INITIAL_VISIBLE_COUNT

    const mouseX = useMotionValue(0)
    const mouseY = useMotionValue(0)

    const springConfig = { damping: 20, stiffness: 300 }
    const x = useSpring(mouseX, springConfig)
    const y = useSpring(mouseY, springConfig)

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            mouseX.set(e.clientX)
            mouseY.set(e.clientY)
        }

        window.addEventListener("mousemove", handleMouseMove)
        return () => window.removeEventListener("mousemove", handleMouseMove)
    }, [mouseX, mouseY])

    const handleProjectClick = (project: (typeof PROJECTS_DATA)[0]) => {
        trackProjectClick(project.title)
    }

    return (
        <section className="relative w-full py-20">
            <div className="container mx-auto px-4">
                <div className="mb-16 border-border/70 border-b pb-4 dark:border-border/40">
                    <span className="font-mono text-muted-foreground text-xs uppercase tracking-widest">
                        Selected Works
                    </span>
                </div>

                <div className="relative flex flex-col">
                    {displayedProjects.map((project, index) => (
                        <ProjectDialog
                            key={project.id}
                            project={project}
                            onClick={() => handleProjectClick(project)}
                            onMouseEnter={() => {
                                setHoveredIndex(index)
                            }}
                            onMouseLeave={() => {
                                setHoveredIndex(null)
                            }}
                        >
                            <div className="group relative flex w-full items-center justify-between border-border/70 border-b py-10 transition-all duration-500 hover:border-foreground/20 hover:px-4 dark:border-border/40">
                                {/* Background highlight on hover */}
                                <div
                                    className={`absolute inset-0 -mx-4 rounded-lg bg-secondary/50 px-4 transition-all duration-300 ease-out ${
                                        hoveredIndex === index
                                            ? "scale-100 opacity-100"
                                            : "scale-95 opacity-0"
                                    }`}
                                />

                                <div className="relative flex flex-1 items-baseline gap-8 text-left">
                                    <span className="font-mono text-muted-foreground text-sm transition-colors group-hover:text-foreground">
                                        {(index + 1)
                                            .toString()
                                            .padStart(2, "0")}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="inline-flex items-center gap-2">
                                            <h2 className="font-medium text-4xl text-foreground/80 tracking-tighter transition-all duration-300 group-hover:text-foreground sm:text-6xl">
                                                {project.title}
                                            </h2>
                                            {/* Arrow that slides in */}
                                            <div
                                                className={`flex size-12 items-center justify-center rounded-full border border-border/70 transition-all duration-300 dark:border-border/40 ${
                                                    hoveredIndex === index
                                                        ? "translate-x-0 translate-y-0 border-foreground bg-foreground text-background opacity-100"
                                                        : "-translate-x-2 translate-y-2 opacity-0"
                                                }`}
                                            >
                                                <HugeiconsIcon
                                                    icon={ArrowUpRightIcon}
                                                    size={20}
                                                    strokeWidth={1.5}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="relative flex items-center gap-6">
                                    <span
                                        className={`hidden font-mono text-xs uppercase tracking-wider transition-all duration-300 sm:block ${
                                            hoveredIndex === index
                                                ? "text-foreground"
                                                : "text-muted-foreground"
                                        }`}
                                    >
                                        {project.category}
                                    </span>
                                </div>
                            </div>
                        </ProjectDialog>
                    ))}
                </div>

                {hasMoreProjects && (
                    <div className="mt-8 flex justify-center">
                        <button
                            type="button"
                            onClick={() => setShowAllProjects(!showAllProjects)}
                            className="group inline-flex items-center gap-2 rounded-full border border-border/70 px-4 py-2.5 text-muted-foreground transition-all duration-300 hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60 dark:border-border/40"
                        >
                            <HugeiconsIcon
                                icon={
                                    showAllProjects
                                        ? ArrowUp01Icon
                                        : ArrowDown01Icon
                                }
                                size={18}
                                strokeWidth={1.5}
                                className="transition-transform duration-300"
                            />
                            <span className="font-mono text-xs uppercase tracking-widest">
                                {showAllProjects
                                    ? "View Less"
                                    : `View ${PROJECTS_DATA.length - INITIAL_VISIBLE_COUNT} More`}
                            </span>
                        </button>
                    </div>
                )}

                {/* Floating Image Portal - Crossfade between projects */}
                {showImageOnHover && hoveredIndex !== null && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{
                            duration: 0.3,
                            ease: "easeOut",
                        }}
                        style={{
                            x,
                            y,
                            translateX: "20px",
                            translateY: "-50%",
                        }}
                        className="pointer-events-none fixed top-0 left-0 z-50 hidden h-[380px] w-[600px] overflow-hidden rounded-sm shadow-2xl lg:block"
                    >
                        <div className="relative h-full w-full bg-muted">
                            {displayedProjects.map((project, imgIndex) => (
                                <Image
                                    key={project.id}
                                    src={project.images[0]}
                                    alt={project.title}
                                    fill
                                    className={`object-cover transition-all duration-500 ease-out ${
                                        hoveredIndex === imgIndex
                                            ? "scale-100 opacity-100"
                                            : "scale-110 opacity-0 blur-sm"
                                    }`}
                                    style={{
                                        filter:
                                            hoveredIndex === imgIndex
                                                ? "none"
                                                : "blur(10px)",
                                    }}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        </section>
    )
}
